import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import { inverseGoldenRatio } from '@glass/common/modules/goldenRatio/goldenRatio';
import Card from '@glass/web/components/base/Card';
import CardActionArea from '@glass/web/components/base/CardActionArea';
import CardHeader from '@glass/web/components/base/CardHeader';
import Skeleton from '@glass/web/components/base/Skeleton';
import { FLEX, SPACE_BETWEEN } from '@glass/web/modules/theme/flexConstants';
import makeStyles from '@glass/web/modules/theme/makeStyles';

import AspectRatio from '@glass/shared/components/AspectRatio/AspectRatio';
import ResponsiveImage from '@glass/shared/components/Image/ResponsiveImage';
import ResourceLink from '@glass/shared/components/Link/ResourceLink';

const useStyles = makeStyles()((theme, { bgcolor }) => ({
  root: {
    display: FLEX,
    flexDirection: 'column',
    height: '100%',
    justifyContent: SPACE_BETWEEN,
    backgroundColor: bgcolor,
    color: bgcolor && theme.palette.getContrastText(bgcolor),
    transition: theme.transitions.create(['background', 'font']),
  },
  bold: {
    fontWeight: 500,
  },
}));

function ContentCard({
  image,
  imageTitle,
  avatar,
  title,
  subheader,
  square,
  children,
  subheaderVariant,
  imageSizes,
  variant,
  loading,
  onClick,
  slug,
  className,
  bgcolor,
}) {
  const { classes, cx } = useStyles({ bgcolor });
  const headerClasses = useMemo(() => ({ title: classes.bold }), [classes.bold]);
  const subheaderTypographyProps = useMemo(
    () => ({ variant: subheaderVariant, color: 'inherit' }),
    [subheaderVariant],
  );
  return (
    <Card className={cx(className, classes.root)} square={square} variant={variant}>
      <CardHeader
        avatar={loading ? null : avatar}
        classes={headerClasses}
        subheader={subheader}
        subheaderTypographyProps={subheaderTypographyProps}
        title={
          !title && loading ? (
            <span>
              <Skeleton />
              <Skeleton />
              <Skeleton width={`${inverseGoldenRatio}%`} />
            </span>
          ) : (
            <ResourceLink slug={slug} onClick={onClick}>
              {title}
            </ResourceLink>
          )
        }
      />
      <div>
        <AspectRatio height={1} width={1.9}>
          {loading ? (
            <Skeleton height="100%" variant="rectangular" width="100%" />
          ) : (
            <ResourceLink slug={slug} onClick={onClick}>
              <CardActionArea>
                <ResponsiveImage
                  external
                  alt={imageTitle}
                  aspectRatio={1.9}
                  height="100%"
                  path={image}
                  sizes={imageSizes}
                  width="100%"
                />
              </CardActionArea>
            </ResourceLink>
          )}
        </AspectRatio>
        {children}
      </div>
    </Card>
  );
}

ContentCard.propTypes = {
  avatar: PropTypes.node,
  bgcolor: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  imageSizes: PropTypes.string,
  imageTitle: PropTypes.string,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  slug: PropTypes.string,
  square: PropTypes.bool,
  subheader: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  subheaderVariant: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  variant: PropTypes.string,
};

ContentCard.displayName = 'ContentCard';

export default ContentCard;
